/**
 * @fileoverview Target resource API calls.
 * @description Wraps GET /sales/targets, POST /sales/targets (assign), and the target mutations
 * (Edit Target's name/period/priority update, stage, reassign, follow-up, confirm-payment). New, additive API surface — no hook calls this yet; see
 * docs/sales-tenant-api-contract.md for the endpoint contract.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

import type { TargetWithOverdue } from '@/features/sales/mocks/targets-mock-store'
import type { Target } from '@/features/sales/types/tenant.types'

export interface TargetListFilters {
  tenantId?: string
  ownerId?: string
  ownerIds?: string[]
  stage?: Target['stage']
  status?: Target['status']
  periodType?: Target['periodType']
  overdue?: boolean
}

export interface UpdateStageRequest {
  stage: Target['stage']
  reason?: string
  actingUserId: string
}

export interface ReassignTargetRequest {
  toOwnerId: string
  actingUserId: string
  actingUserRole: string
}

export interface UpdateTargetDetailsRequest {
  name: string
  periodType: Target['periodType']
  periodLabel: string
  startDate: string
  endDate: string
  priority: Target['priority']
  actingUserId: string
  actingUserRole: string
}

export interface ScheduleFollowUpRequest {
  nextFollowUpAt: string
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Fetches targets matching the supplied filters, each with its current derived
 * `overdue` flag.
 * @param {TargetListFilters} [filters] - The filters to apply.
 * @returns {Promise<TargetWithOverdue[]>} Every matching target.
 */
export async function getTargets(filters: TargetListFilters = {}): Promise<TargetWithOverdue[]> {
  const { ownerIds, ...rest } = filters
  const { data } = await httpClient.get<TargetWithOverdue[]>('/sales/targets', {
    params: { ...rest, ownerIds: ownerIds?.join(',') },
  })
  return data
}

/**
 * @description Updates a target's sales stage (owner-only; Lost/On Hold require a reason).
 * @param {string} id - The target id to update.
 * @param {UpdateStageRequest} input - The new stage, its acting user, and the reason note.
 * @returns {Promise<TargetWithOverdue>} The updated target.
 */
export async function updateTargetStage(
  id: string,
  input: UpdateStageRequest,
): Promise<TargetWithOverdue> {
  const { data } = await httpClient.patch<TargetWithOverdue>(`/sales/targets/${id}/stage`, input)
  return data
}

/**
 * @description Transfers a target's ownership without changing its dates. A Manager/Head may only
 * reassign within their own permitted hierarchy scope; Super Admin may transfer across Heads.
 * @param {string} id - The target id to reassign.
 * @param {ReassignTargetRequest} input - The new owner and the acting user/role performing the
 * transfer.
 * @returns {Promise<TargetWithOverdue>} The updated target.
 */
export async function reassignTarget(
  id: string,
  input: ReassignTargetRequest,
): Promise<TargetWithOverdue> {
  const { data } = await httpClient.patch<TargetWithOverdue>(`/sales/targets/${id}/reassign`, input)
  return data
}

/**
 * @description Corrects an open target's name, period (with its derived dates) and priority —
 * permitted to the owner's reporting Manager/Head or Super Admin, never the owner themselves.
 * @param {string} id - The target id to update.
 * @param {UpdateTargetDetailsRequest} input - The new values and the acting user/role.
 * @returns {Promise<TargetWithOverdue>} The updated target.
 */
export async function updateTargetDetails(
  id: string,
  input: UpdateTargetDetailsRequest,
): Promise<TargetWithOverdue> {
  const { data } = await httpClient.patch<TargetWithOverdue>(`/sales/targets/${id}`, input)
  return data
}

/**
 * @description Records the next planned follow-up against a target (owner, or an authorised
 * reporting Manager/Head within the owner's hierarchy scope).
 * @param {string} id - The target id to schedule a follow-up against.
 * @param {ScheduleFollowUpRequest} input - The follow-up's date/time and the acting user/role.
 * @returns {Promise<TargetWithOverdue>} The updated target.
 */
export async function scheduleFollowUp(
  id: string,
  input: ScheduleFollowUpRequest,
): Promise<TargetWithOverdue> {
  const { data } = await httpClient.patch<TargetWithOverdue>(
    `/sales/targets/${id}/follow-up`,
    input,
  )
  return data
}

/**
 * @description Confirms initial payment, completing the target and converting its tenant to Active
 * Client. Intended for Super Admin only — the real backend must enforce this via auth; the mock
 * accepts the confirming user id as-is.
 * @param {string} id - The target id whose payment is being confirmed.
 * @param {string} confirmedByUserId - The confirming Super Admin's user id.
 * @returns {Promise<TargetWithOverdue>} The completed target.
 */
export async function confirmTargetPayment(
  id: string,
  confirmedByUserId: string,
): Promise<TargetWithOverdue> {
  const { data } = await httpClient.post<TargetWithOverdue>(
    `/sales/targets/${id}/confirm-payment`,
    {
      confirmedByUserId,
    },
  )
  return data
}
